import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import '../styles/ReviewPhotos.css';

/**
 * <ReviewPhotoLightbox images startIndex onClose />
 *
 * Full-screen photo viewer. Arrow keys + swipe support.
 */
const ReviewPhotoLightbox = ({ images = [], startIndex = 0, onClose, reviewMeta }) => {
  const [idx, setIdx] = useState(startIndex);

  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [prev, next, onClose]);

  // Touch swipe
  const [touchStart, setTouchStart] = useState(null);
  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart == null) return;
    const dx = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(dx) > 50) dx > 0 ? prev() : next();
    setTouchStart(null);
  };

  if (!images.length) return null;
  const img = images[idx];

  return (
    <div className="rp-lightbox" onClick={onClose}>
      <button className="rp-lb-close" onClick={onClose} aria-label="Close">
        <FiX />
      </button>

      {images.length > 1 && (
        <button
          className="rp-lb-nav rp-lb-prev"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Previous"
        >
          <FiChevronLeft />
        </button>
      )}

      <div
        className="rp-lb-stage"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img src={img.url} alt={`Review ${idx + 1}`} />
        {reviewMeta && (
          <div className="rp-lb-meta">
            <div className="rp-lb-meta-rating">
              {'★'.repeat(reviewMeta.rating || 5)}
              <span className="rp-lb-meta-sep">·</span>
              <span>{reviewMeta.authorName}</span>
            </div>
            {reviewMeta.comment && (
              <div className="rp-lb-meta-comment">"{reviewMeta.comment}"</div>
            )}
          </div>
        )}
      </div>

      {images.length > 1 && (
        <button
          className="rp-lb-nav rp-lb-next"
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Next"
        >
          <FiChevronRight />
        </button>
      )}

      {images.length > 1 && (
        <div className="rp-lb-counter">
          {idx + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

export default ReviewPhotoLightbox;
